import { imageAccept } from "../../utils/validateImage";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import apiClient from "../../api/ApiClients";
import Sidebar from "../../components/layout/Sidebar";
import validateImage from "../../utils/validateImage";

const BlogFormPage = () => {
  const { id } = useParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    subTitle: "",
    content: "",
    categoryId: "",
    thumbnail: null,
    gallery: [],
    oldImages: [],
  });
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [existingGallery, setExistingGallery] = useState([]);
  const [existingThumbnail, setExistingThumbnail] = useState(null);
  const thumbnailInputRef = useRef();
  const galleryInputRef = useRef();
  const navigate = useNavigate();

  // Fetch categories
  useEffect(() => {
    apiClient
      .request("blog-category", "GET")
      .then((res) => setCategories(res.blogCategories || []));
  }, []);

  // Fetch blog if editing
  useEffect(() => {
    if (id) {
      apiClient.request(`blog/${id}`, "GET").then((res) => {
        const blog = res.blog;
        setForm({
          title: blog.title || "",
          subTitle: blog.subTitle || "",
          content: blog.content || "",
          categoryId: blog.categoryId || "",
          thumbnail: null,
          gallery: [],
          oldImages: [],
        });
        setExistingThumbnail(blog.thumbnail || null);
        setExistingGallery(blog.gallery || []);
        // Load HTML content into editor
        if (blog.content) {
          const blocksFromHtml = htmlToDraft(blog.content);
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
      });
    }
    setLoading(false);
  }, [id]);

  // Update form.content when editor changes
  const handleEditorStateChange = (state) => {
    setEditorState(state);
    setForm((prev) => ({
      ...prev,
      content: draftToHtml(convertToRaw(state.getCurrentContent())),
    }));
  };

  // Handle input changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle category select
  const handleCategoryChange = (option) => {
    setForm({ ...form, categoryId: option ? option.value : "" });
  };

  // Handle thumbnail upload
  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validation = await validateImage(file);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }
    if (existingThumbnail && typeof existingThumbnail === "string") {
      setForm((prev) => ({
        ...prev,
        oldImages: [...prev.oldImages, existingThumbnail],
      }));
      setExistingThumbnail(null);
    }
    setForm((prev) => ({ ...prev, thumbnail: file }));
  };

  // Remove thumbnail
  const handleRemoveThumbnail = () => {
    if (existingThumbnail) {
      setForm((prev) => ({
        ...prev,
        oldImages: [...prev.oldImages, existingThumbnail],
      }));
      setExistingThumbnail(null);
    }
    setForm((prev) => ({ ...prev, thumbnail: null }));
  };

  // Handle gallery upload
  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files);
    let validFiles = [];
    for (let file of files) {
      const validation = await validateImage(file);
      if (!validation.isValid) {
        toast.error(validation.error);
        continue;
      }
      validFiles.push(file);
    }
    setForm((prev) => ({
      ...prev,
      gallery: [...prev.gallery, ...validFiles],
    }));
  };

  // Remove gallery image
  const handleRemoveGalleryImage = (img, isExisting) => {
    if (isExisting) {
      setExistingGallery((prev) => prev.filter((g) => g !== img));
      setForm((prev) => ({
        ...prev,
        oldImages: [...prev.oldImages, img],
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        gallery: prev.gallery.filter((g) => g !== img),
      }));
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.title.trim() ||
      !form.subTitle.trim() ||
      !form.content.trim() ||
      !form.categoryId ||
      (!form.thumbnail && !existingThumbnail)
    ) {
      toast.error("All required fields must be filled");
      return;
    }
    const data = new FormData();
    data.append("title", form.title);
    data.append("subTitle", form.subTitle);
    data.append("content", form.content);
    data.append("categoryId", form.categoryId);
    if (form.thumbnail) data.append("thumbnail", form.thumbnail);
    if (form.oldImages.length > 0)
      form.oldImages.forEach((img) => data.append("oldImages", img));
    form.gallery.forEach((file) => data.append("gallery", file));
    existingGallery.forEach((img) => data.append("gallery", img));

    try {
      if (id) {
        await apiClient.request(`blog/${id}`, "PUT", data, true);
        toast.success("Blog updated");
      } else {
        await apiClient.request("blog", "POST", data, true);
        toast.success("Blog added");
      }
      navigate("/admin/blogs");
    } catch (e) {
      toast.error("Failed to save blog");
    }
  };

  if (loading) {
    return (
      <>
        <Sidebar />
        <div className="wrapper" style={{ padding: "0px" }}>
          <Card className="m-4 p-4 text-center">
            <div>Loading...</div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="wrapper" style={{ padding: "0px" }}>
        <Card className="m-4 p-4">
          <h3>{id ? "Edit Blog" : "Add Blog"}</h3>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                name="title"
                value={form.title}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sub Title *</Form.Label>
              <Form.Control
                name="subTitle"
                value={form.subTitle}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content *</Form.Label>
              <div style={{ minHeight: 200 }}>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={handleEditorStateChange}
                  editorStyle={{
                    minHeight: 180,
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    padding: 10,
                    background: "#fff",
                  }}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category *</Form.Label>
              <Select
                value={
                  categories
                    .filter((cat) => cat._id === form.categoryId)
                    .map((cat) => ({ value: cat._id, label: cat.name }))[0] ||
                  null
                }
                onChange={handleCategoryChange}
                options={categories.map((cat) => ({
                  value: cat._id,
                  label: cat.name,
                }))}
                placeholder="Select category"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Thumbnail *</Form.Label>
              <div>
                {existingThumbnail && (
                  <div style={{ marginBottom: 10 }}>
                    <img
                      src={existingThumbnail}
                      alt="Thumbnail"
                      style={{ width: 100, height: "auto" }}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={handleRemoveThumbnail}
                      style={{ marginLeft: 10 }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                {form.thumbnail && (
                  <div style={{ marginBottom: 10 }}>
                    <img
                      src={
                        typeof form.thumbnail === "string"
                          ? form.thumbnail
                          : URL.createObjectURL(form.thumbnail)
                      }
                      alt="Thumbnail"
                      style={{ width: 100, height: "auto" }}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={handleRemoveThumbnail}
                      style={{ marginLeft: 10 }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                <Button
                  variant="outline-primary"
                  onClick={() => thumbnailInputRef.current.click()}
                >
                  Upload Thumbnail
                </Button>
                <Form.Control
                  ref={thumbnailInputRef}
                  type="file"
                  accept={imageAccept}
                  style={{ display: "none" }}
                  onChange={handleThumbnailChange}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Gallery (optional)</Form.Label>
              <Row>
                {existingGallery.map((img, idx) => (
                  <Col key={idx} xs={3} className="mb-2">
                    <img
                      src={
                        typeof img === "string" ? img : URL.createObjectURL(img)
                      }
                      alt="Gallery"
                      style={{ width: "100%", height: 80, objectFit: "cover" }}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleRemoveGalleryImage(img, true)}
                      style={{ marginTop: 5 }}
                    >
                      Remove
                    </Button>
                  </Col>
                ))}
                {form.gallery.map((file, idx) => (
                  <Col key={idx} xs={3} className="mb-2">
                    <img
                      src={
                        typeof file === "string"
                          ? file
                          : URL.createObjectURL(file)
                      }
                      alt="Gallery"
                      style={{ width: "100%", height: 80, objectFit: "cover" }}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleRemoveGalleryImage(file, false)}
                      style={{ marginTop: 5 }}
                    >
                      Remove
                    </Button>
                  </Col>
                ))}
              </Row>
              <Button
                variant="outline-primary"
                onClick={() => galleryInputRef.current.click()}
              >
                Add Gallery Images
              </Button>
              <Form.Control
                ref={galleryInputRef}
                type="file"
                accept={imageAccept}
                multiple
                style={{ display: "none" }}
                onChange={handleGalleryChange}
              />
            </Form.Group>
            <div className="text-end">
              <Button type="submit" variant="primary">
                {id ? "Update" : "Add"}
              </Button>
              <Button
                variant="secondary"
                className="ms-2"
                onClick={() => navigate("/admin/blogs")}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card>
      </div>
      <ToastContainer />
    </>
  );
};

export default BlogFormPage;
